import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, Loader2, ArrowUpRight, ShieldCheck } from "lucide-react";
import { createLead } from "@/lib/api";

const CHECKS = ["Speed & performance", "Mobile-friendliness", "Local SEO", "Security & SSL"];

export const HealthCheck = () => {
  const [form, setForm] = useState({ hasWebsite: "", website: "", phone: "", email: "", name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Please enter your email.");
      return;
    }
    if (!form.hasWebsite) {
      toast.error("Please let us know if you have a website.");
      return;
    }
    if (form.hasWebsite === "yes" && !form.website) {
      toast.error("Please enter your website address.");
      return;
    }
    setSubmitting(true);
    try {
      const siteLine = form.hasWebsite === "no"
        ? "Has a website: No (needs a new site)"
        : `Has a website: Yes — ${form.website || "(not provided)"}`;
      await createLead({
        name: form.name || "Website Health Check",
        email: form.email,
        phone: form.phone || "",
        company: "",
        service: "Free Website Health Check",
        budget: "",
        message: `Free Website Health Check request. ${siteLine}`,
      });
      setDone(true);
      toast.success("Request received! We'll send your free audit shortly.");
      setForm({ hasWebsite: "", website: "", phone: "", email: "", name: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-[#0d0d0f] border border-line px-4 py-3.5 text-white placeholder:text-text-2/60 focus:outline-none focus:border-accent transition-colors";

  return (
    <section data-testid="health-check-section" className="relative py-24 sm:py-32 border-t border-line">
      <div className="container-x">
        <div className="relative border border-accent/40 bg-surface overflow-hidden">
          <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent" />
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left */}
            <div className="p-8 lg:p-12">
              <span className="mono-label text-accent flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Free Website Health Check
              </span>
              <h2 className="font-head font-black tracking-tighter text-4xl sm:text-5xl mt-5 text-balance">
                Is your website costing you customers?
              </h2>
              <p className="text-text-2 text-lg leading-relaxed mt-5 max-w-md">
                Get a free, no-obligation audit of your current site. We'll check what's holding you
                back and show you exactly how to fix it.
              </p>
              <ul className="mt-7 grid grid-cols-2 gap-3">
                {CHECKS.map((c) => (
                  <li key={c} className="flex items-center gap-2 text-sm text-white/85">
                    <Search className="w-3.5 h-3.5 text-accent" /> {c}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — form */}
            <div className="p-8 lg:p-12 bg-surface-2 border-t lg:border-t-0 lg:border-l border-line">
              {done ? (
                <div data-testid="health-check-success" className="h-full flex flex-col items-center justify-center text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-accent/15 border border-accent flex items-center justify-center mb-6">
                    <ShieldCheck className="w-7 h-7 text-accent" />
                  </div>
                  <h3 className="font-head font-bold text-2xl">Audit on the way!</h3>
                  <p className="text-text-2 mt-2 max-w-xs">We'll review your site and email your free health check within 1 business day.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="health-check-form">
                  <div>
                    <label className="mono-label text-text-2 block mb-2">Do you have a website? *</label>
                    <div className="flex gap-2">
                      {["yes", "no"].map((v) => (
                        <button
                          type="button"
                          key={v}
                          data-testid={`hc-haswebsite-${v}`}
                          onClick={() => setForm({ ...form, hasWebsite: v, website: v === "no" ? "" : form.website })}
                          className={`flex-1 py-3 border font-head font-semibold transition-colors ${
                            form.hasWebsite === v ? "border-accent text-accent bg-accent/10" : "border-line text-text-2 hover:border-white/30"
                          }`}
                        >
                          {v === "yes" ? "Yes" : "No"}
                        </button>
                      ))}
                    </div>
                  </div>

                  {form.hasWebsite === "yes" && (
                    <div>
                      <label className="mono-label text-text-2 block mb-2">What is your website? *</label>
                      <input data-testid="hc-website" className={inputCls} placeholder="yourbusiness.com" value={form.website} onChange={set("website")} />
                    </div>
                  )}

                  <div>
                    <label className="mono-label text-text-2 block mb-2">Phone number</label>
                    <input data-testid="hc-phone" type="tel" className={inputCls} placeholder="(916) 555-0123" value={form.phone} onChange={set("phone")} />
                  </div>

                  <div>
                    <label className="mono-label text-text-2 block mb-2">Email *</label>
                    <input data-testid="hc-email" type="email" className={inputCls} placeholder="you@email.com" value={form.email} onChange={set("email")} />
                  </div>

                  <div>
                    <label className="mono-label text-text-2 block mb-2">Name (optional)</label>
                    <input data-testid="hc-name" className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="health-check-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-accent text-ink font-head font-bold py-4 hover:bg-white transition-colors disabled:opacity-60"
                  >
                    {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : <>Get my free audit <ArrowUpRight className="w-5 h-5" /></>}
                  </button>
                  <p className="mono-label text-text-2 text-center !text-[0.6rem] !tracking-normal !normal-case">No spam. No obligation. Just a helpful audit.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
